"use client";

// src/ui/components/calculator/reverse-recipe.tsx
//
// Reverse-mode "mixing helper". Replaces the BAC-water field when the user
// knows only the vial size + the dose they want. Shows two profile cards
// ("Leichter ablesbar" = more water/clearer mark; "Kleinere Injektion" = less
// fluid) and a green recipe card driven by calcReverse() that tells the user
// exactly how much water to add and how many marks to draw. See design §1.6.

import { Check } from "lucide-react";
import { useTranslations } from "next-intl";
import { SyringeSvg } from "@/ui/components/calculator/syringe-svg";
import { calcReverse } from "@/lib/calculator/calculations";
import { Label } from "@/ui/components/ui/label";
import type { ReverseProfile } from "@/lib/calculator/types";

interface ReverseRecipeProps {
	vial: number;
	dose: number;
	profile: ReverseProfile;
	onProfileChange: (profile: ReverseProfile) => void;
	/** Per-peptide accent class for the syringe + IU highlight (e.g. "text-success"). */
	accentClassName?: string;
}

export function ReverseRecipe({
	vial,
	dose,
	profile,
	onProfileChange,
	accentClassName = "text-success",
}: ReverseRecipeProps) {
	const t = useTranslations("peptideCalculator");
	const rev = calcReverse({ vial, dose, profile });
	const bacLabel = rev ? rev.bac.toFixed(1).replace(".", ",") : null;

	const profiles: { id: ReverseProfile; title: string; sub: string }[] = [
		{ id: "easier", title: t("reverse.profileEasier"), sub: t("reverse.profileEasierSub") },
		{ id: "smaller", title: t("reverse.profileSmaller"), sub: t("reverse.profileSmallerSub") },
	];

	return (
		<div className="space-y-2">
			<Label className="text-xs sm:text-sm">{t("reverse.profilesLabel")}</Label>

			{/* Profile cards — replace the BAC-water field in reverse mode */}
			<div role="radiogroup" aria-label={t("reverse.profilesLabel")} className="grid grid-cols-2 gap-2">
				{profiles.map((p) => {
					const active = profile === p.id;
					return (
						<button
							key={p.id}
							type="button"
							role="radio"
							aria-checked={active}
							onClick={() => onProfileChange(p.id)}
							className={[
								"flex min-h-14 flex-col items-start justify-center rounded-xl border px-3 py-2 text-left transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 active:scale-[0.99]",
								active
									? "bg-primary/5 border-primary shadow-sm"
									: "hover:border-primary/50 border-border bg-card",
							].join(" ")}
						>
							<span className={`text-sm font-semibold ${active ? "text-primary" : "text-foreground"}`}>
								{p.title}
							</span>
							<span className="mt-0.5 text-[11px] text-muted-foreground">{p.sub}</span>
						</button>
					);
				})}
			</div>

			{/* Recipe card (green) or graceful "no clean mark" hint */}
			{rev ? (
				<div className="border-success/30 bg-success/10 rounded-xl border p-3">
					<div className="text-[11px] font-semibold uppercase tracking-wide text-success">
						{t("reverse.recipeTitle")}
					</div>
					<p className="mt-1.5 text-sm leading-relaxed text-foreground">
						{t("reverse.recipeWater")} <b className="font-mono font-semibold tabular-nums">{bacLabel} ml</b>{" "}
						{t("reverse.recipeWaterTail")}
						<br />
						{t("reverse.recipeDraw")}{" "}
						<b className={`font-mono font-semibold tabular-nums ${accentClassName}`}>
							{rev.iu} {t("reverse.marks")}
						</b>
						.
					</p>
					<div className="mt-2">
						<SyringeSvg
							syringeSize={rev.syringe}
							fillMl={rev.vol}
							accentClassName={accentClassName}
							className="w-full"
							compact
						/>
					</div>
					<div className="mt-1 inline-flex items-center gap-1.5 text-xs font-medium text-success">
						<Check aria-hidden className="h-3.5 w-3.5 shrink-0" />
						<span>{t("reverse.cleanStrike")}</span>
					</div>
				</div>
			) : (
				<div className="bg-muted/40 rounded-xl border border-border p-3">
					<div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
						{t("reverse.noClean")}
					</div>
					<p className="mt-1 text-sm leading-relaxed text-muted-foreground">{t("reverse.noCleanHint")}</p>
				</div>
			)}
		</div>
	);
}
